import React, { useEffect, useMemo, useState } from "react";

// ============================================================================
// CONSTANTS & TYPES
// ============================================================================

const PFLICHT_KEYS = [
  "pflicht_notenDatenbank",
  "pflicht_zeiterfassung",
  "pflicht_absenzen",
  "pflicht_protimeAbsenzen",
  "pflicht_ausbildungskontrolle",
  "pflicht_auftraege",
  "pflicht_outlook",
  "pflicht_abmachungen",
  "pflicht_verhaltenSchule",
];

const PFLICHT_COMMENT_KEYS = {
  pflicht_notenDatenbank: "pflicht_notenDatenbankComment",
  pflicht_zeiterfassung: "pflicht_zeiterfassungComment",
  pflicht_absenzen: "pflicht_absenzenComment",
  pflicht_protimeAbsenzen: "pflicht_protimeAbsenzenComment",
  pflicht_ausbildungskontrolle: "pflicht_ausbildungskontrolleComment",
  pflicht_auftraege: "pflicht_auftraegeComment",
  pflicht_outlook: "pflicht_outlookComment",
  pflicht_abmachungen: "pflicht_abmachungenComment",
  pflicht_verhaltenSchule: "pflicht_verhaltenSchuleComment",
};

const PFLICHT_LABELS_DE = [
  { key: "pflicht_notenDatenbank", label: "Führen der Notendatenbank:" },
  {
    key: "pflicht_zeiterfassung",
    label: "Führen der Zeiterfassung (proTime) / Einhaltung von Arbeitszeiten:",
  },
  { key: "pflicht_absenzen", label: "Meldung von Absenzen:" },
  {
    key: "pflicht_protimeAbsenzen",
    label:
      "Korrektes ausfüllen vom proTime bei Absenzen (Unfall, Krankheiten):",
  },
  {
    key: "pflicht_ausbildungskontrolle",
    label:
      "Ausbildungskontrolle (Lernjournal, time2learn/Journey, Lerndokumentation etc.):",
  },
  { key: "pflicht_auftraege", label: "Erledigung der Aufträge:" },
  {
    key: "pflicht_outlook",
    label:
      "Führen des Outlook Kalenders (Schultage, Ferien, ÜK, Sitzungen etc.):",
  },
  { key: "pflicht_abmachungen", label: "Einhaltung von Abmachungen:" },
  {
    key: "pflicht_verhaltenSchule",
    label:
      "Einwandfreies Verhalten in der Berufsfachschule (keine unentschuldigte Absenz, keine Ermahnungen/Verweise o.ä):",
  },
];

const PFLICHT_LABELS_EN = [
  { key: "pflicht_notenDatenbank", label: "Maintaining the grade database:" },
  {
    key: "pflicht_zeiterfassung",
    label: "Time tracking (proTime) / Compliance with working hours:",
  },
  { key: "pflicht_absenzen", label: "Reporting absences:" },
  {
    key: "pflicht_protimeAbsenzen",
    label: "Correct filling of proTime for absences (accident, illness):",
  },
  {
    key: "pflicht_ausbildungskontrolle",
    label:
      "Training control (learning journal, time2learn/Journey, learning documentation etc.):",
  },
  { key: "pflicht_auftraege", label: "Completion of assignments:" },
  {
    key: "pflicht_outlook",
    label:
      "Maintaining Outlook calendar (school days, holidays, courses, meetings etc.):",
  },
  { key: "pflicht_abmachungen", label: "Compliance with agreements:" },
  {
    key: "pflicht_verhaltenSchule",
    label:
      "Impeccable conduct at vocational school (no unexcused absences, no warnings/reprimands etc.):",
  },
];

export const emptyLeistungsbeurteilungValues = {
  firstName: "",
  lastName: "",
  year: "",
  department: "",
  overallRating: "",
  pflichtPercent: 0,
  motivationPercent: null,
  averagePercent: 0,
  pflicht_notenDatenbank: "",
  pflicht_zeiterfassung: "",
  pflicht_absenzen: "",
  pflicht_protimeAbsenzen: "",
  pflicht_ausbildungskontrolle: "",
  pflicht_auftraege: "",
  pflicht_auftraegeComment: "",
  pflicht_outlook: "",
  pflicht_outlookComment: "",
  pflicht_abmachungen: "",
  pflicht_abmachungenComment: "",
  pflicht_verhaltenSchule: "",
  pflicht_verhaltenSchuleComment: "",
  pflicht_notenDatenbankComment: "",
  pflicht_zeiterfassungComment: "",
  pflicht_absenzenComment: "",
  pflicht_protimeAbsenzenComment: "",
  pflicht_ausbildungskontrolleComment: "",
  motivationText: "",
  goalsNextSemester: "",
  remarks: "",
  assessmentDate: "",
  trainerName: "",
};

// ============================================================================
// CALCULATION HELPERS
// ============================================================================

/**
 * Maps number of "Ja" answers to percentage for Pflichterfüllung.
 * 1–5 Ja → 0%
 * 6 Ja   → 50%
 * 7 Ja   → 80%
 * 8 Ja   → 100%
 * 9 Ja   → 150%
 */
function mapJaToPercent(countJa) {
  if (countJa >= 9) return 150;
  if (countJa === 8) return 100;
  if (countJa === 7) return 80;
  if (countJa === 6) return 50;
  return 0;
}

/**
 * Pure function to recalculate derived values (pflichtPercent, averagePercent).
 */
function recalcDerived(state) {
  const yesCount = PFLICHT_KEYS.reduce(
    (acc, key) => acc + (state[key] === "Ja" ? 1 : 0),
    0,
  );
  const pflichtPercent = mapJaToPercent(yesCount);

  const motivationPercent =
    state.motivationPercent === null || Number.isNaN(state.motivationPercent)
      ? null
      : Number(state.motivationPercent);

  const averagePercent =
    motivationPercent === null
      ? pflichtPercent
      : Math.round((pflichtPercent + motivationPercent) / 2);

  // Auto-calculate overall rating based on averagePercent
  // A: 120-150, B: 90-115, C: 10-85, D: check "0%"
  let overallRating = "";
  if (averagePercent !== null && averagePercent !== undefined) {
    if (averagePercent >= 120) overallRating = "A";
    else if (averagePercent >= 90) overallRating = "B";
    else if (averagePercent >= 10) overallRating = "C";
    else overallRating = "D";
  }

  return {
    ...state,
    pflichtPercent,
    averagePercent,
    overallRating,
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

export const LeistungsbeurteilungForm = ({
  value,
  onChange,
  readOnly,
  learnerFirstName,
  learnerLastName,
  language = "de",
  errors = {},
  smartGoalPlaceholder = "",
  previousAnswers = {},
  yearPlaceholder = "",
}) => {
  const [form, setForm] = useState(() => recalcDerived(value));

  const currentPflichtLabels =
    language === "en" ? PFLICHT_LABELS_EN : PFLICHT_LABELS_DE;

  const previousScalePlaceholders = useMemo(() => {
    const placeholders = {};
    Object.entries(previousAnswers || {}).forEach(([key, value]) => {
      const previousValue = value?.scaleValue;
      if (typeof previousValue === "string" && previousValue.trim() !== "") {
        placeholders[key] =
          language === "en"
            ? `Previously: ${previousValue}`
            : `Vorher: ${previousValue}`;
      }
    });
    return placeholders;
  }, [previousAnswers, language]);

  const previousCommentPlaceholders = useMemo(() => {
    const placeholders = {};
    Object.entries(previousAnswers || {}).forEach(([key, value]) => {
      const previousComment = value?.comment;
      if (
        typeof previousComment === "string" &&
        previousComment.trim() !== ""
      ) {
        placeholders[key] =
          language === "en"
            ? `Previous: ${previousComment}`
            : `Vorher: ${previousComment}`;
      }
    });
    return placeholders;
  }, [previousAnswers, language]);

  const inlineErrorClass = "text-red-600 text-xs font-semibold mt-1";

  // Sync with parent value + prefill learner names
  useEffect(() => {
    let updated = { ...value };
    let hasChanges = false;

    if (!updated.firstName && learnerFirstName) {
      updated.firstName = learnerFirstName;
      hasChanges = true;
    }
    if (!updated.lastName && learnerLastName) {
      updated.lastName = learnerLastName;
      hasChanges = true;
    }
    if (!updated.assessmentDate) {
      updated.assessmentDate = new Date().toISOString().split("T")[0];
      hasChanges = true;
    }

    // Only recalculate derived if input values that affect it have changed?
    // Actually, recalcDerived is pure based on state.
    // If we just loaded 'value' from parent, we should ensure derived fields are correct.
    const withDerived = recalcDerived(updated);

    // Check if derived actually changed anything important to avoid loop
    if (
      withDerived.overallRating !== value.overallRating ||
      withDerived.pflichtPercent !== value.pflichtPercent ||
      withDerived.averagePercent !== value.averagePercent
    ) {
      hasChanges = true;
    }

    if (hasChanges) {
      setForm(withDerived);
      // Only notify parent if meaningful changes happened to avoid loop if parent updates 'value' reference
      if (onChange) onChange(withDerived);
    } else {
      setForm(withDerived);
    }
  }, [value, learnerFirstName, learnerLastName, onChange]); // Removed onChange to avoid stale closure or loop, but actually usually fine.

  // Update helpers
  const updateField = (key, val) => {
    const next = { ...form, [key]: val };
    const withDerived = recalcDerived(next);
    setForm(withDerived);
    onChange?.(withDerived);
  };

  const handleTextChange = (key) => (e) => updateField(key, e.target.value);

  const handleNumberChange = (key) => (e) => {
    const raw = e.target.value;
    const num = raw === "" ? null : Number(String(raw).replace(",", "."));
    updateField(key, num);
  };

  const handleYesNoChange = (key, val) => () => {
    if (readOnly) return;
    updateField(key, val);
  };

  // Displays
  const allPflichtEmpty = PFLICHT_KEYS.every((key) => form[key] === "");
  const hasMotivation =
    form.motivationPercent !== null && form.motivationPercent !== undefined;

  const pflichtDisplay = allPflichtEmpty ? "TBD%" : `${form.pflichtPercent}%`;
  const motivationDisplay = hasMotivation
    ? `${form.motivationPercent}%`
    : "TBD%";
  const avgDisplay =
    allPflichtEmpty && !hasMotivation ? "TBD%" : `${form.averagePercent}%`;

  // Count for progress
  const yesCount = useMemo(
    () =>
      PFLICHT_KEYS.reduce((acc, key) => acc + (form[key] === "Ja" ? 1 : 0), 0),
    [form],
  );

  // Corporate base classes
  const pageWrap = "min-h-screen bg-ice-white";
  const container = "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8";
  const card = "card-brand overflow-hidden";
  const header = "bg-sunrise-red text-white";
  const sectionTitle = "text-sm font-bold flex items-center";
  const sectionBody = "p-5";
  const label = "block text-xs font-semibold text-grey-dusk mb-2";
  const input =
    "w-full rounded-lg border border-pale-dawn bg-white px-3 py-2 text-sm text-grey-dusk shadow-sm focus:outline-none focus:ring-2 focus:ring-sunrise-red focus:border-sunrise-red disabled:bg-gray-100";
  const textarea =
    "w-full rounded-lg border border-pale-dawn bg-white px-3 py-2 text-sm text-grey-dusk min-h-[92px] resize-y shadow-sm focus:outline-none focus:ring-2 focus:ring-sunrise-red focus:border-sunrise-red disabled:bg-gray-100";

  // Small UI helpers
  const Section = useMemo(
    () =>
      ({ icon, title, required, errorKey, children, right }) => (
        <section className={`${card} ${errorKey ? "ring-2 ring-red-300" : ""}`}>
          <div className={`${header} px-5 py-3`}>
            <div className="flex items-center justify-between gap-3">
              <div className={sectionTitle}>
                <i className={`${icon} mr-2`}></i>
                {title}
                {required ? (
                  <span className="ml-2 text-white/80">*</span>
                ) : null}
              </div>
              {right ? (
                <div className="flex items-center gap-2">{right}</div>
              ) : null}
            </div>
          </div>
          <div className={sectionBody}>{children}</div>
        </section>
      ),
    [card, header, sectionTitle, sectionBody],
  );

  const StatCard = useMemo(
    () =>
      ({ icon, labelText, valueText, hint }) => (
        <div className="bg-ice-white border border-pale-dawn rounded-xl p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-gray-500">{labelText}</div>
              <div className="text-xl font-bold text-grey-dusk mt-1">
                {valueText}
              </div>
              {hint ? (
                <div className="text-xs text-gray-500 mt-1">{hint}</div>
              ) : null}
            </div>
            <div className="w-10 h-10 rounded-xl bg-white border border-pale-dawn flex items-center justify-center text-sunrise-red">
              <i className={icon}></i>
            </div>
          </div>
        </div>
      ),
    [],
  );

  const Pill = useMemo(
    () =>
      ({ active, onClick, children, variant = "primary" }) => {
        const activeCls =
          variant === "primary"
            ? "bg-sunrise-red text-white border-sunrise-red"
            : "bg-grey-dusk text-white border-grey-dusk";

        return (
          <button
            type="button"
            onClick={onClick}
            disabled={readOnly}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              active
                ? activeCls
                : "bg-white text-grey-dusk border-pale-dawn hover:border-sunrise-red/40"
            } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {children}
          </button>
        );
      },
    [readOnly],
  );

  return (
    <div className={pageWrap}>
      <div className={container}>
        {/* Page Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-clipboard-check text-sunrise-red mr-4"></i>
                  Team Assessment Apprentices
                </h1>
                <p className="text-slate-cloud">
                  {language === "en"
                    ? "Performance assessment and development interview."
                    : "Leistungsbeurteilung und Entwicklungsgespräch."}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full lg:w-auto">
                <StatCard
                  icon="fas fa-list-check"
                  labelText={language === "en" ? "Duty" : "Pflichterfüllung"}
                  valueText={pflichtDisplay}
                  hint={`${yesCount}/${PFLICHT_KEYS.length} ${language === "en" ? "Yes" : "Ja"}`}
                />
                <StatCard
                  icon="fas fa-bolt"
                  labelText="Motivation"
                  valueText={motivationDisplay}
                />
                <StatCard
                  icon="fas fa-chart-line"
                  labelText={language === "en" ? "Average" : "Durchschnitt"}
                  valueText={avgDisplay}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Master Data */}
          <Section
            icon="fas fa-id-card"
            title={language === "en" ? "Master Data" : "Stammdaten"}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>
                  {language === "en" ? "First Name" : "Vorname"}
                </label>
                <input className={input} value={form.firstName} disabled />
              </div>

              <div>
                <label className={label}>
                  {language === "en" ? "Last Name" : "Name"}
                </label>
                <input className={input} value={form.lastName} disabled />
              </div>

              <div>
                <label className={label}>
                  {language === "en" ? "Apprenticeship Year" : "Lehrjahr"}
                </label>
                <input
                  className={input}
                  value={form.year}
                  onChange={handleTextChange("year")}
                  placeholder={
                    !form.year
                      ? yearPlaceholder ||
                        (language === "en" ? "e.g. 1" : "z.B. 1")
                      : ""
                  }
                  disabled={readOnly}
                />
              </div>

              <div>
                <label className={label}>
                  {language === "en" ? "Department" : "Abteilung"}
                </label>
                <input
                  className={input}
                  value={form.department}
                  onChange={handleTextChange("department")}
                  placeholder={
                    !form.department
                      ? previousScalePlaceholders.department || ""
                      : ""
                  }
                  disabled={readOnly}
                />
              </div>
            </div>
          </Section>

          {/* Rating levels */}
          <Section
            icon="fas fa-layer-group"
            title={
              language === "en"
                ? "Performance Rating Levels"
                : "Bewertungsstufen der Leistung"
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                {
                  k: "A",
                  r: "120% – 150%",
                  t:
                    language === "en"
                      ? "Exceeds expectations"
                      : "Leistungen uebertroffen die Erwartungen",
                },
                {
                  k: "B",
                  r: "90% – 115%",
                  t:
                    language === "en"
                      ? "Meets expectations"
                      : "Leistungen entsprechen den Erwartungen",
                },
                {
                  k: "C",
                  r: "10% – 85%",
                  t:
                    language === "en"
                      ? "Partially meets expectations"
                      : "Leistungen entsprechen teilweise den Erwartungen",
                },
                {
                  k: "D",
                  r: "0%",
                  t:
                    language === "en"
                      ? "Does not meet expectations"
                      : "Leistungen entsprechen nicht den Erwartungen",
                },
              ].map((x) => (
                <div
                  key={x.k}
                  className="bg-ice-white border border-pale-dawn rounded-xl p-4 flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded-xl bg-white border border-pale-dawn flex items-center justify-center font-bold text-grey-dusk">
                    {x.k}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-grey-dusk">
                      {x.r}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">{x.t}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Overall rating */}
          <Section
            icon="fas fa-star"
            title={
              language === "en" ? "Overall Assessment" : "Gesamtbeurteilung"
            }
            required
            errorKey={errors.overallRating}
            right={
              errors.overallRating ? (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/15 border border-white/20">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  Required
                </span>
              ) : null
            }
          >
            <div className="bg-ice-white border border-pale-dawn rounded-xl p-4">
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-grey-dusk">
                  {language === "en" ? "Duty" : "Pflichterfüllung"}:
                </span>{" "}
                {pflichtDisplay}
                <span className="mx-2 text-gray-300">|</span>
                <span className="font-semibold text-grey-dusk">
                  Motivation:
                </span>{" "}
                {motivationDisplay}
                <span className="mx-2 text-gray-300">|</span>
                <span className="font-semibold text-grey-dusk">
                  {language === "en" ? "Average" : "Durchschnitt"}:
                </span>{" "}
                {avgDisplay}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {["A", "B", "C", "D"].map((rating) => (
                  <Pill
                    key={rating}
                    active={form.overallRating === rating}
                    onClick={() => {}} // Read-only / Auto-calculated
                    variant="primary"
                  >
                    <i className="fas fa-check mr-2 text-[10px] opacity-80"></i>
                    Rating {rating}
                  </Pill>
                ))}
              </div>

              {errors.overallRating && (
                <div className={inlineErrorClass}>{errors.overallRating}</div>
              )}
            </div>
          </Section>

          {/* Assessment */}
          <Section
            icon="fas fa-calendar-check"
            title="Assessment"
            required
            errorKey={errors.assessmentDate}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
              <div className="lg:col-span-3">
                <label className={label}>
                  {language === "en" ? "Assessment Date" : "Datum Assessment"}{" "}
                  <span className="text-sunrise-red">*</span>
                </label>

                <div className="relative">
                  <input
                    type="date"
                    className={`${input} pr-10 ${
                      errors.assessmentDate ? "border-red-500 bg-red-50" : ""
                    }`}
                    value={form.assessmentDate || ""}
                    onChange={handleTextChange("assessmentDate")}
                    disabled={readOnly}
                  />
                  <i className="fas fa-calendar-alt absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm"></i>
                </div>

                {errors.assessmentDate && (
                  <div className={inlineErrorClass}>
                    {errors.assessmentDate}
                  </div>
                )}
              </div>

              <div className="lg:col-span-6">
                <div className="bg-ice-white border border-pale-dawn rounded-xl px-4 py-3 mt-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center flex-shrink-0 ">
                      <i className="fas fa-circle-info"></i>
                    </div>
                    <div className="text-sm text-gray-600 mt-2">
                      {language === "en"
                        ? `This assessment was discussed with ${form.firstName} ${form.lastName}.`
                        : `Die Beurteilung wurde mit ${form.firstName} ${form.lastName} besprochen.`}
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <label className={label}>
                  {language === "en"
                    ? "Responsible Trainer"
                    : "Name Berufsbildungsverantwortliche/r"}
                </label>
                <div className="relative">
                  <input
                    className={`${input} pr-10`}
                    value={form.trainerName}
                    disabled
                  />
                  <i className="fas fa-user-tie absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm"></i>
                </div>
              </div>
            </div>
          </Section>

          {/* Pflicht */}
          <Section
            icon="fas fa-list-check"
            title={
              language === "en"
                ? "1. Observation Criteria (Duty Fulfillment)"
                : "1. Beobachtungskriterien Pflichterfuellung"
            }
            errorKey={errors.pflicht}
            right={
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/15 border border-white/20">
                <i className="fas fa-check-double mr-2"></i>
                {yesCount}/{PFLICHT_KEYS.length}
              </span>
            }
          >
            <div className="space-y-3">
              <div className="bg-ice-white border border-pale-dawn rounded-xl px-4 py-3 text-sm text-gray-600">
                <span className="font-semibold text-grey-dusk">
                  {language === "en" ? "Rule:" : "Regel:"}
                </span>{" "}
                {language === "en"
                  ? "1–5 Yes = 0%, 6 Yes = 50%, 7 Yes = 80%, 8 Yes = 100%, 9 Yes = 150%."
                  : "1–5x Ja = 0%, 6x Ja = 50%, 7x Ja = 80%, 8x Ja = 100%, 9x Ja = 150%."}
              </div>

              <div className="bg-white border border-pale-dawn rounded-xl overflow-hidden">
                {currentPflichtLabels.map(({ key, label: rowLabel }, idx) => (
                  <div
                    key={key}
                    className={`px-4 py-4 ${
                      idx !== 0 ? "border-t border-pale-dawn" : ""
                    }`}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_auto_minmax(320px,1fr)] gap-4 items-start">
                      {/* Label */}
                      <div className="min-w-0">
                        <div className="text-sm text-grey-dusk leading-6">
                          {rowLabel}
                        </div>

                        {!form[key] && previousScalePlaceholders[key] && (
                          <div className="text-xs text-gray-400 italic mt-1">
                            {previousScalePlaceholders[key]}
                          </div>
                        )}
                      </div>

                      {/* Decision pills */}
                      <div className="flex items-center gap-2 flex-wrap lg:flex-nowrap">
                        <Pill
                          active={form[key] === "Ja"}
                          onClick={handleYesNoChange(key, "Ja")}
                          variant="primary"
                        >
                          {language === "en" ? "Yes" : "Ja"}
                        </Pill>

                        <Pill
                          active={form[key] === "Nein"}
                          onClick={handleYesNoChange(key, "Nein")}
                          variant="secondary"
                        >
                          {language === "en" ? "No" : "Nein"}
                        </Pill>

                        <button
                          type="button"
                          onClick={() => updateField(key, "")}
                          disabled={readOnly}
                          title={
                            language === "en"
                              ? "Reset selection"
                              : "Auswahl zuruecksetzen"
                          }
                          className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
                            form[key] === ""
                              ? "bg-gray-50 text-gray-300 border-gray-200"
                              : "bg-white text-gray-500 border-pale-dawn hover:border-sunrise-red/30 hover:text-sunrise-red"
                          } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          <i className="fas fa-rotate-left text-xs"></i>
                        </button>
                      </div>

                      {/* Comment */}
                      <div className="min-w-0">
                        <div className="relative">
                          <textarea
                            className="w-full rounded-xl border border-pale-dawn bg-ice-white/60 px-3 py-2.5 text-sm text-grey-dusk min-h-[72px] resize-y shadow-sm focus:outline-none focus:ring-2 focus:ring-sunrise-red focus:border-sunrise-red disabled:bg-gray-100 placeholder:text-gray-400"
                            value={form[PFLICHT_COMMENT_KEYS[key]] || ""}
                            onChange={handleTextChange(
                              PFLICHT_COMMENT_KEYS[key],
                            )}
                            disabled={readOnly}
                            placeholder={
                              !form[PFLICHT_COMMENT_KEYS[key]]
                                ? previousCommentPlaceholders[key] ||
                                  (language === "en"
                                    ? "Optional note..."
                                    : "Optionale Notiz...")
                                : ""
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between bg-ice-white border border-pale-dawn rounded-xl px-4 py-3">
                <div className="text-sm text-gray-600">
                  {language === "en"
                    ? "Target Achievement (Duty) in %"
                    : "Zielerreichung (Pflicht) in %"}
                </div>
                <div className="text-lg font-bold text-grey-dusk">
                  {pflichtDisplay}
                </div>
              </div>

              {errors.pflicht && (
                <div className={inlineErrorClass}>{errors.pflicht}</div>
              )}
            </div>
          </Section>

          {/* Motivation */}
          <Section
            icon="fas fa-bolt"
            title="2. Motivation, Engagement, Image"
            errorKey={errors.motivation}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
              <div className="lg:col-span-8">
                <label className={label}>
                  {language === "en" ? "Observations" : "Beobachtungen"}{" "}
                  <span className="text-sunrise-red">*</span>
                </label>
                <textarea
                  className={`${textarea} ${
                    errors.motivationText ? "border-red-500 bg-red-50" : ""
                  }`}
                  value={form.motivationText}
                  onChange={handleTextChange("motivationText")}
                  disabled={readOnly}
                  placeholder={
                    !form.motivationText
                      ? previousCommentPlaceholders.motivationText ||
                        (language === "en"
                          ? "Add comment (optional)"
                          : "Kommentar hinzufügen (optional)")
                      : ""
                  }
                />
                {errors.motivationText && (
                  <div className={inlineErrorClass}>
                    {errors.motivationText}
                  </div>
                )}
              </div>

              <div className="lg:col-span-4">
                <label className={label}>
                  {language === "en"
                    ? "Target Achievement in %"
                    : "Zielerreichung in %"}{" "}
                  <span className="text-sunrise-red">*</span>
                </label>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    className={`${input} max-w-[140px] ${
                      errors.motivationPercent ? "border-red-500 bg-red-50" : ""
                    }`}
                    value={
                      form.motivationPercent === null
                        ? ""
                        : String(form.motivationPercent)
                    }
                    onChange={handleNumberChange("motivationPercent")}
                    placeholder={
                      form.motivationPercent === null
                        ? previousScalePlaceholders.motivationPercent || ""
                        : ""
                    }
                    disabled={readOnly}
                  />
                  <span className="text-sm text-gray-600">%</span>
                </div>

                {errors.motivationPercent && (
                  <div className={inlineErrorClass}>
                    {errors.motivationPercent}
                  </div>
                )}

                <div className="mt-3 bg-ice-white border border-pale-dawn rounded-xl px-4 py-3">
                  <div className="text-xs text-gray-500">
                    {language === "en" ? "Current value" : "Aktueller Wert"}
                  </div>
                  <div className="text-lg font-bold text-grey-dusk mt-1">
                    {motivationDisplay}
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* Development */}
          <Section
            icon="fas fa-bullseye"
            title={
              language === "en"
                ? "3. Development Interview"
                : "3. Entwicklungsgespräch"
            }
          >
            <label className={label}>
              {language === "en"
                ? "Goals and measures for next semester"
                : "Ziel und Massnahmen für das nächste Semester"}
              {!readOnly && !form.goalsNextSemester && smartGoalPlaceholder && (
                <span className="ml-2 text-xs font-normal text-gray-400 italic">
                  <i className="fas fa-history mr-1"></i>
                  from prev. meeting
                </span>
              )}
            </label>
            <textarea
              className={textarea}
              value={form.goalsNextSemester}
              onChange={handleTextChange("goalsNextSemester")}
              disabled={readOnly}
              placeholder={
                !readOnly && !form.goalsNextSemester
                  ? smartGoalPlaceholder ||
                    previousCommentPlaceholders.goalsNextSemester ||
                    (language === "en"
                      ? "Add comment (optional)"
                      : "Kommentar hinzufügen (optional)")
                  : ""
              }
            />
          </Section>

          {/* Remarks */}
          <Section
            icon="fas fa-comment-dots"
            title={
              language === "en"
                ? "Remarks by Apprentice / Supervisor"
                : "Bemerkungen des Lernenden / der Vorgesetzten"
            }
          >
            <textarea
              className={textarea}
              value={form.remarks}
              onChange={handleTextChange("remarks")}
              disabled={readOnly}
              placeholder={
                !form.remarks
                  ? previousCommentPlaceholders.remarks ||
                    (language === "en"
                      ? "Add comment (optional)"
                      : "Kommentar hinzufügen (optional)")
                  : ""
              }
            />
          </Section>
        </div>
      </div>
    </div>
  );
};
